import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { TouristSpot } from "@/types";
import SectionHeading from "@/components/base/SectionHeading";
import Rating from "@/components/base/Rating";

const MAX_VISIBLE = 8;

export default function TouristTeaser({ spots }: { spots: TouristSpot[] }) {
  const [active, setActive] = useState("All");

  const districts = useMemo(
    () => ["All", ...Array.from(new Set(spots.map((s) => s.district)))],
    [spots],
  );

  const visible = useMemo(() => {
    const list = active === "All" ? spots : spots.filter((s) => s.district === active);
    return list.slice(0, MAX_VISIBLE);
  }, [spots, active]);

  if (!spots.length) return null;

  return (
    <section className="bg-accent-50 border-y border-accent-100">
      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-14 md:py-20">
        <SectionHeading
          eyebrow="Explore by district"
          title="Tourist spots across the Mysore region"
          subtitle="Planning a trip or hosting guests? Discover the heritage, hills and wildlife that make each district special."
          link={{ label: "All tourist spots", to: "/tourist-spots" }}
        />

        <div className="mt-6 flex flex-wrap gap-2">
          {districts.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setActive(d)}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold cursor-pointer ${
                active === d
                  ? "bg-primary-500 text-foreground-950"
                  : "border border-accent-200 bg-background-50/60 text-foreground-700 hover:border-primary-400"
              }`}
            >
              {d === "All" ? "All districts" : d}
            </button>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {visible.map((s) => (
            <Link
              key={s.id}
              to={`/spot/${s.slug}`}
              className="group flex flex-col overflow-hidden rounded-lg border border-accent-100 bg-background-50 cursor-pointer"
            >
              <div className="h-44 w-full overflow-hidden">
                <img
                  src={s.image}
                  alt={s.name}
                  title={`${s.name} ${s.locationName}`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-1 flex-col p-4">
                <h3 className="font-heading text-base font-bold text-foreground-950 leading-snug">{s.name}</h3>
                <div className="mt-1.5">
                  <Rating value={s.rating} />
                </div>
                <p className="mt-2 line-clamp-2 flex-1 text-sm text-foreground-600 leading-relaxed">{s.description}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-accent-700">
                  <i className="ri-map-pin-line"></i> {s.district}
                </span>
                <span className="mt-1 inline-flex items-center gap-1 text-xs text-foreground-500">
                  <i className="ri-sun-line text-accent-600"></i> {s.bestTime}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}